/**
 * R06: Demo Info Panel
 * Dismissible panel showing demo controls, full version features, and purchase CTA.
 */

import { t } from '@/i18n'
import './DemoPanel.css'

export class DemoPanel {
  private panel: HTMLElement
  private reopenBtn: HTMLElement

  constructor(container: HTMLElement) {
    this.panel = this.createPanel()
    this.reopenBtn = this.createReopenButton()
    container.appendChild(this.panel)
    container.appendChild(this.reopenBtn)
  }

  private createPanel(): HTMLElement {
    const panel = document.createElement('div')
    panel.className = 'demo-panel'

    // Header
    const header = document.createElement('div')
    header.className = 'demo-panel-header'
    header.innerHTML = `
      <i class="fa-solid fa-flask"></i>
      <span class="demo-panel-title">${t('demo.panelTitle')}</span>
    `
    const closeBtn = document.createElement('button')
    closeBtn.className = 'demo-panel-close'
    closeBtn.innerHTML = '<i class="fa-solid fa-xmark"></i>'
    closeBtn.addEventListener('click', () => this.dismiss())
    header.appendChild(closeBtn)
    panel.appendChild(header)

    // Body (scrollable)
    const body = document.createElement('div')
    body.className = 'demo-panel-body'

    // Description
    const desc = document.createElement('div')
    desc.className = 'demo-panel-description'
    desc.textContent = t('demo.panelDescription')
    body.appendChild(desc)

    // Controls section
    body.appendChild(this.createSection(t('demo.controlsTitle'), [
      { icon: 'fa-cube', text: t('demo.controlViewMode') },
      { icon: 'fa-eye', text: t('demo.controlLayers') },
      { icon: 'fa-hand', text: t('demo.controlCamera') },
    ]))

    // Features section
    body.appendChild(this.createSection(t('demo.featuresTitle'), [
      { icon: 'fa-check', text: t('demo.featureMap'), feature: true },
      { icon: 'fa-check', text: t('demo.featureZones'), feature: true },
      { icon: 'fa-check', text: t('demo.featureBuildings'), feature: true },
      { icon: 'fa-check', text: t('demo.featureCatalog'), feature: true },
      { icon: 'fa-check', text: t('demo.featureSave'), feature: true },
      { icon: 'fa-check', text: t('demo.featureUndo'), feature: true },
    ]))

    panel.appendChild(body)

    // Footer with buy button
    const footer = document.createElement('div')
    footer.className = 'demo-panel-footer'
    const buyBtn = document.createElement('a')
    buyBtn.className = 'demo-buy-btn'
    buyBtn.href = t('demo.purchaseUrl')
    buyBtn.target = '_blank'
    buyBtn.rel = 'noopener'
    buyBtn.innerHTML = `<i class="fa-solid fa-cart-shopping"></i> ${t('demo.buyButton')}`
    footer.appendChild(buyBtn)
    panel.appendChild(footer)

    return panel
  }

  private createSection(label: string, items: { icon: string; text: string; feature?: boolean }[]): HTMLElement {
    const section = document.createElement('div')
    section.className = 'demo-panel-section'

    const sectionLabel = document.createElement('div')
    sectionLabel.className = 'demo-panel-section-label'
    sectionLabel.textContent = label
    section.appendChild(sectionLabel)

    for (const item of items) {
      const row = document.createElement('div')
      row.className = item.feature ? 'demo-panel-item feature' : 'demo-panel-item'
      row.innerHTML = `<i class="fa-solid ${item.icon}"></i><span>${item.text}</span>`
      section.appendChild(row)
    }

    return section
  }

  private createReopenButton(): HTMLElement {
    const btn = document.createElement('button')
    btn.className = 'demo-reopen-btn'
    btn.setAttribute('data-tooltip', t('demo.reopenTooltip'))
    btn.innerHTML = '<i class="fa-solid fa-flask"></i>'
    btn.addEventListener('click', () => this.reopen())
    return btn
  }

  private dismiss(): void {
    this.panel.classList.add('dismissed')
    // After transition, show reopen button
    setTimeout(() => {
      this.reopenBtn.style.display = 'flex'
    }, 200)
  }

  private reopen(): void {
    this.reopenBtn.style.display = 'none'
    this.panel.classList.remove('dismissed')
  }
}
